import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { FC, useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'reactstrap';
import {
  REPORT_ORIGIN_LIFECYCLE_SURVEYS,
  REPORT_ORIGIN_PARAM,
} from 'views/CustomSurveys/SharedSurveyReportsPage';
import Page from 'views/Layout/Pages/Page';
import CustomSurveyReportView from './CustomSurveyReportView';

interface RouteParams {
  itemId: string;
}

// Where "Back to reports" goes, keyed by the origin marker on the link that
// opened this page. An unknown marker (or none at all) means no back link -
// e.g. the survey-admin builder opens a report in a new tab, where there's
// nothing to go back to.
const BACK_URLS: Record<
  string,
  (formatMessage: IntlShape['formatMessage']) => string
> = {
  [REPORT_ORIGIN_LIFECYCLE_SURVEYS]: (formatMessage) =>
    consts.TEAM_LIFECYCLE_SURVEYS(formatMessage).path,
};

// Standalone, read-only report page reachable by anyone an item is shared
// with, independent of the survey-admin area. Access is enforced
// server-side by the item-scoped endpoints CustomSurveyReportView calls.
const CustomSurveyReportPage: FC = () => {
  const { itemId } = useParams<RouteParams>();
  const { formatMessage } = useIntl();
  const location = useLocation();

  const backUrl = useMemo(() => {
    const origin = new URLSearchParams(location.search).get(
      REPORT_ORIGIN_PARAM
    );
    return origin ? BACK_URLS[origin]?.(formatMessage) : undefined;
  }, [location.search, formatMessage]);

  return (
    <Page>
      <div className="mt-4">
        {backUrl && (
          <div className="mb-3">
            <Button color="link" className="ps-0" tag={Link} to={backUrl}>
              <Icon className="me-1" icon={consts.ICONS.ARROW_LEFT} />
              <FormattedMessage
                id="app.views.custom_surveys.shared_reports.back"
                defaultMessage="Back to reports"
              />
            </Button>
          </div>
        )}
        <CustomSurveyReportView itemId={Number(itemId)} showTitle />
      </div>
    </Page>
  );
};

export default CustomSurveyReportPage;
